/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { animated, SpringConfig, useSpring } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import clamp from 'lodash/clamp';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  Config,
  env,
  selectCameraMode,
  selectCameraPosition,
  selectFollowZoomStops,
  selectHorses,
  selectRightPanelOpen,
  State,
  useTranslation,
} from '~/app';
import boardImg from '~/images/board.jpg';
import woodImg from '~/images/wood.jpg';
import { BAR_TOP_HEIGHT_PERCENT, FAB_EDGE_INSET_REM } from '~/overlay/overlaySpacing';
import { useOnStateChange, vh, vw } from '~/utils';

import { requestInitialCamera, setCameraMode } from './actions';
import BoardCameraControls from './BoardCameraControls';
import { recordCameraSpringRetarget } from './cameraDebug';
import { saveFollowZoomStops } from './cameraStorage';
import {
  bootstrapScaleFromFollowStops,
  CameraTarget,
  computeCameraTravelMetric,
  mapTravelToCameraSpring,
  MAX_SCALE,
  MIN_SCALE,
} from './cameraZoom';
import FieldMarkerView from './FieldMarkerView';
import Horse from './Horse';
import HorsePathsView from './HorsePathsView';
import HurdleView from './HurdleView';
import { Horse as HorseModel } from './reducer';

/**
 * 1: -50vw = offset from left screen to left board
 * 2: 100vw = absolute width of board
 * 3: + 50vw = half of viewport
 *
 * @param x position as percentage of board width
 */
const calculateCamX = (x: number) => `calc((-50vw - ${x} * 100vw) + 50vw)`;

/**
 * 1: calculate padding between screen top and board top
 * 2: 100vw / 3072 * 2400: absolute height of board
 * 3: + 50vh: half of viewport
 *
 * @param y position as percentage of board width
 */
const calculateCamY = (y: number) =>
  `calc(((200vw / ${vw} * ${vh}) - (100vw / ${vw} * ${vh})) / -2 - ${y} * (100vw / ${vw} * ${vh}) + 50vh)`;

const Board = (): JSX.Element => {
  const analytics = useFirebaseAnalytics();
  const { t } = useTranslation();

  const horses = useSelector<State, HorseModel[]>(selectHorses);
  const rightPanelOpen = useSelector(selectRightPanelOpen);

  const transform = (x: number, y: number, scale: number) =>
    `scale3d(${scale}, ${scale}, 1) translate3d(${calculateCamX(x)}, ${calculateCamY(y)}, 0)`;
  const dispatch = useDispatch();
  const cameraMode = useSelector(selectCameraMode);
  const followZoomStops = useSelector(selectFollowZoomStops);
  const cameraManualEnabled = cameraMode === 'fixed';

  /** Request initial follow-camera recompute on mount so first frame uses deterministic camera path. */
  useEffect(() => {
    dispatch(requestInitialCamera());
  }, [dispatch]);

  /** Persist follow zoom factor when it changes (follow mode only; manual zoom is not persisted). */
  useEffect(() => {
    saveFollowZoomStops(followZoomStops);
  }, [followZoomStops]);

  /**
   * Animate camera translation and zoom.
   * In follow mode, start from a stable bootstrap (center + persisted zoom) and animate to the first
   * resolved target so initial load does not snap; after one frame we use Redux camera as target.
   */
  const { x, y, scale } = useSelector(selectCameraPosition);
  const [manScale, setManScale] = useState(scale);
  const [dragPos, setDragPos] = useState(() => ({ x, y }));
  const [hasShownBootstrap, setHasShownBootstrap] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const boardRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => setHasShownBootstrap(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const followBootstrap = useMemo<[number, number, number]>(
    () => [0.5, 0.5, bootstrapScaleFromFollowStops(followZoomStops)],
    [followZoomStops],
  );

  const manualTransform: [number, number, number] = cameraManualEnabled
    ? [dragPos.x, dragPos.y, manScale]
    : [x, y, scale];

  const followTarget = useMemo<[number, number, number]>(
    () => (hasShownBootstrap ? [x, y, scale] : followBootstrap),
    [followBootstrap, hasShownBootstrap, scale, x, y],
  );

  /** Last rendered camera pose; used for travel-adaptive spring on retarget. */
  const lastCameraRef = useRef<CameraTarget>({ x, y, scale });

  const prevFollowTargetRef = useRef(followTarget);
  useEffect(() => {
    if (cameraManualEnabled) return;
    if (prevFollowTargetRef.current === followTarget) return;
    prevFollowTargetRef.current = followTarget;
    recordCameraSpringRetarget();
  }, [cameraManualEnabled, followTarget]);

  const [springFrom, setSpringFrom] = useState<[number, number, number]>(followTarget);
  const springConfig = useMemo((): SpringConfig => {
    if (cameraManualEnabled || prefersReducedMotion) return { duration: 0 };
    const from: CameraTarget = {
      x: springFrom[0],
      y: springFrom[1],
      scale: springFrom[2],
    };
    const target: CameraTarget = {
      x: followTarget[0],
      y: followTarget[1],
      scale: followTarget[2],
    };
    const travel = computeCameraTravelMetric(from, target);
    return mapTravelToCameraSpring(travel);
  }, [cameraManualEnabled, followTarget, prefersReducedMotion, springFrom]);

  const { cameraTransform } = useSpring({
    from: {
      cameraTransform: springFrom,
    },
    to: {
      cameraTransform: cameraManualEnabled ? manualTransform : followTarget,
    },
    config: springConfig,
    onChange: ({ value }) => {
      const [nx, ny, ns] = value.cameraTransform;
      lastCameraRef.current = { x: nx, y: ny, scale: ns };
    },
  });

  /**
   * Dragging gestures for manual camera control
   */
  useOnStateChange(
    cameraManualEnabled,
    useCallback(
      (isManualMode) => {
        const [nextX, nextY, nextScale] = cameraTransform.get();
        lastCameraRef.current = { x: nextX, y: nextY, scale: nextScale };
        setSpringFrom([nextX, nextY, nextScale]);
        if (!isManualMode) return;
        setDragPos({ x: nextX, y: nextY });
        setManScale(nextScale);
      },
      [cameraTransform],
    ),
  );

  useGesture(
    {
      onDragStart: () => {
        if (!cameraManualEnabled) dispatch(setCameraMode('fixed'));
      },
      onWheelStart: () => {
        if (!cameraManualEnabled) dispatch(setCameraMode('fixed'));
      },
      onDrag: ({ down, movement, memo = cameraTransform.get() }) => {
        // tbd: make more natural
        if (!down) return;
        const newX = clamp(memo[0] - movement[0] / window.innerWidth / manScale, -0.25, 1.25);
        const newY = clamp(memo[1] - movement[1] / window.innerHeight / manScale, -0.25, 1.25);
        setDragPos({ x: newX, y: newY });
        return memo;
      },
      onWheel: ({ pinching, delta }) => {
        const factor = pinching ? 0.005 : 0.001;
        const dy = -delta[1];
        setManScale((manScale) => clamp(manScale + dy * factor, MIN_SCALE, MAX_SCALE));
      },
      onPinch: () => {},
    },
    {
      drag: { delay: true, filterTaps: true, preventDefault: true },
      wheel: { preventDefault: true },
      pinch: { preventDefault: true },
      eventOptions: { passive: false },
      target: boardRootRef,
    },
  );

  useOnStateChange(
    cameraManualEnabled,
    useCallback(
      (newCameraManualEnabled) => {
        if (newCameraManualEnabled) {
          analytics?.logEvent('set_camera_manual');
        } else {
          analytics?.logEvent('set_camera_auto');
        }
      },
      [analytics],
    ),
  );

  useEffect(() => {
    const callback: EventListener = (e) => e.preventDefault();
    document.addEventListener('gesturestart', callback);
    document.addEventListener('gesturechange', callback);
    return () => {
      document.removeEventListener('gesturestart', callback);
      document.removeEventListener('gesturechange', callback);
    };
  }, []);

  return (
    <>
      <animated.div
        ref={boardRootRef}
        className="no-select flex h-[calc(200vw/3072*2400)] w-[200vw] origin-[50vw_50vh] cursor-grab touch-none items-center justify-center overflow-hidden active:cursor-grabbing"
        style={{
          transform: cameraTransform.to(transform),
        }}
      >
        <img
          src={woodImg}
          draggable={false}
          className="absolute inset-0 h-full w-full object-fill"
          alt={t('game_backgroundAlt')}
        />

        <div className="absolute h-[calc(100vw/3072*2400)] w-screen">
          <img
            src={boardImg}
            draggable={false}
            className="absolute h-full w-full object-cover"
            alt={t('game_boardAlt')}
          />

          <div className="absolute inset-0 h-full w-full bg-red-300/20">
            <HurdleView />
            <HorsePathsView />
            {horses.map((_horse, index) => (
              <Horse key={index} index={index} />
            ))}
            {env.dev && Config.enableMarkerView && <FieldMarkerView />}
          </div>
        </div>
      </animated.div>

      {/* Top-right camera control cluster: vertical by default, horizontal when chat panel is open */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 flex flex-col items-end justify-start"
        style={{
          top: `calc(${BAR_TOP_HEIGHT_PERCENT}% + ${FAB_EDGE_INSET_REM}rem)`,
          right: `${FAB_EDGE_INSET_REM}rem`,
        }}
      >
        <BoardCameraControls
          onManualZoomIn={() => setManScale((s) => clamp(s + 0.05, MIN_SCALE, MAX_SCALE))}
          onManualZoomOut={() => setManScale((s) => clamp(s - 0.05, MIN_SCALE, MAX_SCALE))}
          isManualZoomInDisabled={manScale >= MAX_SCALE}
          isManualZoomOutDisabled={manScale <= MIN_SCALE}
          horizontal={rightPanelOpen}
        />
      </div>
    </>
  );
};

export default Board;
